import { Button, Form, Input, message, Select, Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { apiRequest } from "../../utils/apiClient";
import {
  QuestionCircleOutlined,
  MailOutlined,
  LockOutlined,
  HeatMapOutlined,
  UserOutlined,
  ShopOutlined,
  PhoneOutlined,
  NumberOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const Register = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Watch invoice type
  const invoiceType = Form.useWatch("invoiceType", form);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      await apiRequest(
        "/api/auth/register",
        {
          method: "POST",
          body: JSON.stringify(values),
        },
        false
      );
      message.success(t("registrationSuccess"));
      navigate("/login");
    } catch (error) {
      message.error(t("somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="select-none min-h-screen bg-[#f4f8ff] px-4 py-6 sm:px-8 lg:px-12 lg:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-6xl items-center gap-8 xl:gap-16">
        <div className="w-full lg:w-[42%] xl:w-[38%]">
          <div className="mx-auto max-w-2xl">
            <h1 className="mt-10 mb-8 text-3xl font-semibold tracking-tight text-gray-900 leading-tight">
              {t("Welcome! Let’s Begin")}
            </h1>

            <Form layout="vertical" onFinish={onFinish} form={form} className="space-y-1">
            {/* Username + Email */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("username")}</span>}
                name="userName"
                rules={[{ required: true }]}
                className="mb-4 flex-1"
              >
                <Input
                  size="large"
                  prefix={<UserOutlined className="text-[#808080] mr-1" />}
                  placeholder="Enter your username"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("email")}</span>}
                name="email"
                rules={[{ required: true }]}
                className="mb-4 flex-1"
              >
                <Input
                  size="large"
                  prefix={<MailOutlined className="text-[#808080] mr-1" />}
                  placeholder="someone@example.com"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>
            </div>

            {/* Business Name + Phone */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("businessName")}</span>}
                name="businessName"
                rules={[{ required: true }]}
                className="mb-4 flex-1"
              >
                <Input
                  size="large"
                  prefix={<ShopOutlined className="text-[#808080] mr-1" />}
                  placeholder="Enter your business name"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("businessPhone")}</span>}
                name="businessPhone"
                rules={[{ required: true }]}
                className="mb-4 flex-1"
              >
                <Input
                  size="large"
                  prefix={<PhoneOutlined className="text-[#808080] mr-1" />}
                  placeholder="03XX1234567"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>
            </div>

            {/* Sales Tax + NTN */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("salesTaxNumber")}</span>}
                name="salesTaxNumber"
                rules={[{ required: true }]}
                className="mb-4 flex-1"
              >
                <Input
                  size="large"
                  prefix={<NumberOutlined className="text-[#808080] mr-1" />}
                  placeholder="Enter sales tax number"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("ntnNumber")}</span>}
                name="ntn"
                rules={[{ required: true }]}
                className="mb-4 flex-1"
              >
                <Input
                  size="large"
                  prefix={<IdcardOutlined className="text-[#808080] mr-1" />}
                  placeholder="Enter NTN number"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>
            </div>

            {/* Invoice Type Dropdown */}
            <Form.Item
              label={<span className="text-sm font-medium text-[#2f2f2f]">{t("invoiceType")}</span>}
              name="invoiceType"
              rules={[{ required: true, message: t("pleaseSelectInvoiceType") }]}
              className="mb-4"
            >
              <Select
                size="large"
                placeholder={t("selectInvoiceTypePlaceholder")}
                className="[&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-0 [&_.ant-select-selector]:!bg-[#EEE] [&_.ant-select-selector]:!shadow-none"
              >
                <Option value="DIGITAL_INVOICING">{t("digitalInvoicing")}</Option>
                <Option value="POS">{t("posRetailer")}</Option>
              </Select>
            </Form.Item>

            {/* Conditional Digital Invoicing Fields */}
            {invoiceType === "DIGITAL_INVOICING" && (
              <div className="flex flex-col sm:flex-row gap-4">
                <Form.Item
                  label={<span className="text-sm font-medium text-[#2f2f2f]">{t("cnic")}</span>}
                  name="cnic"
                  rules={[{ required: true }]}
                  className="mb-4 flex-1"
                >
                  <Input
                    size="large"
                    prefix={<IdcardOutlined className="text-[#808080] mr-1" />}
                    placeholder={t("cnicPlaceholderReg")}
                    className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                  />
                </Form.Item>

                <Form.Item
                  label={<span className="text-sm font-medium text-[#2f2f2f]">{t("pages:province")}</span>}
                  name="province"
                  rules={[{ required: true }]}
                  className="mb-4 flex-1"
                >
                  <Select
                    size="large"
                    placeholder={t("pages:selectProvince")}
                    className="[&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-0 [&_.ant-select-selector]:!bg-[#EEE] [&_.ant-select-selector]:!shadow-none"
                  >
                    <Option value="Punjab">Punjab</Option>
                    <Option value="Sindh">Sindh</Option>
                    <Option value="KPK">KPK</Option>
                    <Option value="Balochistan">Balochistan</Option>
                    <Option value="Islamabad">Islamabad</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span className="text-sm font-medium text-[#2f2f2f]">{t("address")}</span>}
                  name="address"
                  rules={[{ required: true }]}
                  className="mb-4 flex-1"
                >
                  <Input
                    size="large"
                    prefix={<EnvironmentOutlined className="text-[#808080] mr-1" />}
                    placeholder={t("businessAddress")}
                    className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                  />
                </Form.Item>
              </div>
            )}

            {/* Passwords */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Form.Item
                label={
                  <span>
                    {t("password")}{" "}
                    <Tooltip
                      title={
                        <div className="text-xs">
                          <div className="mb-1 font-semibold">{t("passwordRulesTitle")}</div>
                          <div>• {t("passwordRuleLength")}</div>
                          <div>• {t("passwordRuleUpper")}</div>
                          <div>• {t("passwordRuleLower")}</div>
                          <div>• {t("passwordRuleNum")}</div>
                          <div>• {t("passwordRuleSpecial")}</div>
                          <div className="mt-2 font-semibold">{t("passwordExample")}</div>
                        </div>
                      }
                    >
                      <QuestionCircleOutlined className="text-gray-400" />
                    </Tooltip>
                  </span>
                }
                name="password"
                rules={[
                  { required: true, message: t("pleaseEnterPassword") },
                  {
                    min: 8,
                    message: t("passwordMinLength"),
                  },
                  {
                    pattern: /[A-Z]/,
                    message: t("passwordUppercase"),
                  },
                  {
                    pattern: /[a-z]/,
                    message: t("passwordLowercase"),
                  },
                  {
                    pattern: /[0-9]/,
                    message: t("passwordNumber"),
                  },
                  {
                    pattern: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
                    message: t("passwordSpecial"),
                  },
                ]}
                className="mb-4 flex-1"
                extra={
                  <div className="text-xs text-gray-500">
                    {t("passwordExtra")}
                  </div>
                }
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined className="text-[#808080] mr-1" />}
                  placeholder={t("enterStrongPassword")}
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>

              <Form.Item
                label={t("confirmPassword")}
                name="passwordAgain"
                dependencies={["password"]}
                rules={[
                  { required: true, message: t("pleaseConfirmPassword") },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t("passwordsDoNotMatch"))
                      );
                    },
                  }),
                ]}
                className="mb-4 flex-1"
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined className="text-[#808080] mr-1" />}
                  placeholder={t("reEnterPassword")}
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>
            </div>

            <Form.Item className="mb-4">
              <Button
                type="primary"
                htmlType="submit"
                className="font-bold !h-11 !w-full !rounded-lg !bg-[#1677ff] !text-white hover:!bg-[#0e66e1]"
                loading={loading}
              >
                {t("register")}
              </Button>
            </Form.Item>
          </Form>

          <div className="mb-6 mt-5 flex flex-col gap-1 text-sm text-[#555]">
            <span>
              {t("alreadyHaveAccount")}
              <Link to="/login" className="ml-1 font-medium text-[#1677ff] hover:text-[#0e66e1]">
                {t("loginNow")}
              </Link>
            </span>
          </div>

            <a
              href="https://www.vareonofficial.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-[250px]"
            >
              <div className="flex items-center justify-center gap-2 rounded-xl bg-[#eee] py-3 transition-all duration-300 hover:shadow-md">
                <HeatMapOutlined style={{ fontSize: 25 }} />
                <p className="ml-1 text-sm font-medium text-gray-700">
                  Powered by <span className="text-black">VAREON</span>
                </p>
              </div>
            </a>
          </div>
        </div>

        <div className="relative hidden h-[88vh] min-h-[680px] flex-1 overflow-hidden rounded-[36px] bg-gradient-to-br from-[#1677ff] via-[#2c7df0] to-[#0b52c4] p-10 text-white lg:block xl:p-12">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.22),transparent_35%),radial-gradient(circle_at_12%_18%,rgba(255,255,255,0.15),transparent_30%)]" />

          <div className="relative flex h-full flex-col justify-between">
            <div className="max-w-md pt-4">
              <p className="mb-3 text-base text-[#dfeeff]">VAREON - Point of Sale</p>
              <h2 className="mb-4 text-4xl font-semibold leading-tight text-white">
                Pakistan&apos;s No. 1 POS and Accounting Software
              </h2>
              <p className="mb-5 text-sm leading-7 text-[#eaf3ff]">
                Manage billing, inventory, expenses, receivables, and business reports in one
                simple platform. Built for Pakistani businesses that need speed, accuracy, and
                full control.
              </p>
              <p className="text-base text-[#f2f7ff]">
                Trusted by retailers, restaurants, pharmacies, and wholesalers across Pakistan.
              </p>
            </div>

            <div className="relative max-w-[520px] rounded-[28px] bg-white/20 px-8 py-9 backdrop-blur-sm">
              <h3 className="mb-4 text-4xl font-semibold leading-tight text-white">
                Everything Your Business Needs, In One Place
              </h3>
              <p className="max-w-md text-sm leading-7 text-[#eaf3ff]">
                Fast checkout, smart stock control, supplier and customer ledgers, and real-time
                sales insights to help you grow with confidence every day.
              </p>
              <div className="mt-6 inline-flex rounded-full bg-white px-4 py-2 text-sm font-medium text-[#0f4dc5]">
                Complete POS + Accounts + Reports
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;